import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import type { SalesReportData, Pipeline } from "@re/shared";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Settings, Save, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const PIPELINE_STORAGE_KEY = "selectedPipelines";

export function getSelectedPipelines(): string[] {
  try {
    const stored = localStorage.getItem(PIPELINE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function setSelectedPipelines(pipelineIds: string[]): void {
  localStorage.setItem(PIPELINE_STORAGE_KEY, JSON.stringify(pipelineIds));
}

export default function SettingsPage() {
  const { toast } = useToast();
  const [selectedPipelines, setSelected] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  const { data, isLoading } = useQuery<SalesReportData>({
    queryKey: ["/api/report"],
  });

  useEffect(() => {
    if (data?.availablePipelines) {
      const stored = getSelectedPipelines();
      if (stored.length > 0) {
        setSelected(new Set(stored));
      } else {
        setSelected(new Set(data.availablePipelines.map(p => p.id)));
      }
    }
  }, [data?.availablePipelines]);

  const handleToggle = (pipelineId: string) => {
    const newSet = new Set(selectedPipelines);
    if (newSet.has(pipelineId)) {
      newSet.delete(pipelineId);
    } else {
      newSet.add(pipelineId);
    }
    setSelected(newSet);
    setHasChanges(true);
  };

  const handleSelectAll = () => {
    if (data?.availablePipelines) {
      setSelected(new Set(data.availablePipelines.map(p => p.id)));
      setHasChanges(true);
    }
  };

  const handleSelectNone = () => {
    setSelected(new Set());
    setHasChanges(true);
  };

  const handleSave = () => {
    setSelectedPipelines(Array.from(selectedPipelines));
    setHasChanges(false);
    toast({
      title: "Settings saved",
      description: "Your pipeline selection has been updated. Refresh the dashboard to see the changes.",
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <Settings className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading pipelines...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pipelines = data?.availablePipelines || [];

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <Settings className="h-6 w-6" />
        <h1 className="text-2xl font-bold" data-testid="text-settings-title">Settings</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pipeline Selection</CardTitle>
          <CardDescription>
            Choose which Commusoft sales pipelines to include in your reports.
            Only selected pipelines will appear in the dashboard metrics.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              data-testid="button-select-all"
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectNone}
              data-testid="button-select-none"
            >
              Select None
            </Button>
          </div>

          <div className="space-y-3">
            {pipelines.length === 0 ? (
              <p className="text-muted-foreground">No pipelines available. Connect to BigQuery to see your Commusoft pipelines.</p>
            ) : (
              pipelines.map((pipeline) => (
                <div key={pipeline.id} className="flex items-center space-x-3">
                  <Checkbox
                    id={pipeline.id}
                    checked={selectedPipelines.has(pipeline.id)}
                    onCheckedChange={() => handleToggle(pipeline.id)}
                    data-testid={`checkbox-pipeline-${pipeline.id}`}
                  />
                  <Label
                    htmlFor={pipeline.id}
                    className="text-sm font-medium cursor-pointer"
                  >
                    {pipeline.name}
                  </Label>
                </div>
              ))
            )}
          </div>

          <div className="mt-6 pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || selectedPipelines.size === 0}
              data-testid="button-save-settings"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </Button>
            {selectedPipelines.size === 0 && (
              <p className="text-sm text-destructive mt-2">
                Please select at least one pipeline.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
